import type { FC } from "react";
import { useMemo } from "react";

import { CardGroup } from "@similarweb/pro-logic-components";

import { NOT_AVAILABLE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { useSentimentOverviewData } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sentiment-report/hooks/useSentimentOverviewData";
import { SentimentCard } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sentiment-report/sentiment-overview/SentimentCard";
import type { ICardData } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/sentiment-report/sentiment-overview/types";
import { getBaseWidgetRequestParams } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ILLMReportWidget } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { ReportStatus } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

export const SentimentOverview: FC<ILLMReportWidget> = ({ campaign, campaignConfig, reportStatus, reportDuration }) => {
    const canLoadData = reportStatus === ReportStatus.Ready && !!campaign;
    const {
        data,
        isLoading: isLoadingData,
        isError,
    } = useSentimentOverviewData(getBaseWidgetRequestParams(campaign, campaignConfig, reportDuration), {
        enabled: canLoadData,
    });

    const totalMentions = data?.summary?.total ?? 0;
    const hasMultipleBrands = campaign?.brands?.length > 1;

    const createCardData = (
        titleKey: string,
        tooltipKey: string,
        textKey: string,
        promptsCount: number,
    ): ICardData => ({
        promptsCount,
        title: translate(titleKey),
        tooltip: translate(tooltipKey),
        text: translate(textKey, {
            mentions: promptsCount.toLocaleString(),
            totalMentions: totalMentions.toLocaleString(),
            brand: campaign?.brands?.[0] ?? NOT_AVAILABLE,
        }),
    });

    const cardsData = useMemo<ICardData[]>(
        () => [
            createCardData(
                "llm.sentiment_analysis.overview.card.positive.title",
                "llm.sentiment_analysis.overview.card.positive.tooltip",
                hasMultipleBrands
                    ? "llm.sentiment_analysis.overview.card.positive.text.multiple_brands"
                    : "llm.sentiment_analysis.overview.card.positive.text",
                data?.summary?.positive ?? 0,
            ),
            createCardData(
                "llm.sentiment_analysis.overview.card.neutral.title",
                "llm.sentiment_analysis.overview.card.neutral.tooltip",
                hasMultipleBrands
                    ? "llm.sentiment_analysis.overview.card.neutral.text.multiple_brands"
                    : "llm.sentiment_analysis.overview.card.neutral.text",
                data?.summary?.neutral ?? 0,
            ),
            createCardData(
                "llm.sentiment_analysis.overview.card.negative.title",
                "llm.sentiment_analysis.overview.card.negative.tooltip",
                hasMultipleBrands
                    ? "llm.sentiment_analysis.overview.card.negative.text.multiple_brands"
                    : "llm.sentiment_analysis.overview.card.negative.text",
                data?.summary?.negative ?? 0,
            ),
        ],
        [data],
    );

    const hasData = !!data && !isError && reportStatus !== ReportStatus.Error;
    const shouldShowLoading = (isLoadingData || !campaign) && reportStatus !== ReportStatus.Error;

    return (
        <CardGroup
            data-automation="sentiment-overview-widget"
            $height="120px"
        >
            {cardsData.map((card) => (
                <SentimentCard
                    {...card}
                    key={card.title}
                    totalMentions={totalMentions}
                    isLoading={shouldShowLoading}
                    hasData={hasData}
                />
            ))}
        </CardGroup>
    );
};
